import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from './cn.js';
import { Label } from './label.js';
/**
 * One labelled form row. Owns the id wiring: the label points at the control,
 * the hint and the validation message are both referenced by
 * `aria-describedby`, and the control is marked `aria-invalid` while its
 * message is showing. The error says how to fix, in `destructive-text` with
 * a 14 px icon.
 */
export function Field({ label, hint, error, required, layout = 'stacked', className, children }) {
    const id = React.useId();
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
    const labelId = `${id}-label`;
    const labelNode = (_jsxs(Label, { id: labelId, htmlFor: id, children: [label, required && (_jsx("span", { "aria-hidden": "true", className: "ml-0.5 text-destructive-text", children: "*" }))] }));
    const hintNode = hint ? (_jsx("p", { id: hintId, className: "text-caption text-muted-foreground", children: hint })) : null;
    const errorNode = error ? (_jsxs("p", { id: errorId, className: "flex items-center gap-1 text-caption text-destructive-text", children: [_jsx(CircleAlert, { className: "size-3.5 shrink-0", "aria-hidden": "true" }), error] })) : null;
    const control = children({ id, 'aria-describedby': describedBy, 'aria-labelledby': labelId, invalid: !!error });
    if (layout === 'row') {
        return (_jsxs("div", { "data-slot": "field", className: cn('flex flex-col gap-1.5', className), children: [_jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-0.5", children: [labelNode, hintNode] }), _jsx("div", { className: "shrink-0", children: control })] }), errorNode] }));
    }
    return (_jsxs("div", { "data-slot": "field", className: cn('flex flex-col gap-1.5', className), children: [labelNode, control, hintNode, errorNode] }));
}
const columnClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
    auto: 'grid-cols-[repeat(auto-fit,minmax(220px,1fr))]',
};
/**
 * Label over value in responsive columns: a gateway's readout, a lock's
 * details, a card's identity. An empty value renders as "—".
 */
export function DescriptionList({ items, columns = 'auto', bordered = false, className }) {
    return (_jsx("dl", { "data-slot": "description-list", className: cn('grid', bordered ? 'gap-0 overflow-hidden rounded-lg border' : cn('gap-x-6 gap-y-2', columnClass[columns]), bordered && columns !== 1 && columns !== 'auto' && columnClass[columns], className), children: items.map((item, index) => (_jsxs("div", { className: cn('min-w-0', bordered
                ? 'flex gap-4 border-b border-border px-3 py-2 last:border-b-0'
                : 'flex flex-col gap-0.5'), children: [_jsx("dt", { className: cn('text-caption text-muted-foreground', bordered && 'w-[140px] shrink-0 self-center'), children: item.label }), _jsx("dd", { className: "min-w-0 text-ui break-words compact:text-body", children: item.value == null || item.value === '' ? '—' : item.value })] }, index))) }));
}
/** A short run of monospaced evidence — a UID, a MAC, a SAM identifier. */
export function Code({ children, className }) {
    return (_jsx("code", { className: cn('rounded-sm bg-muted px-1 py-0.5 text-code', className), children: children }));
}
/** Section divider with a caption. */
export function SectionDivider({ children, className }) {
    return (_jsxs("div", { className: cn('flex items-center gap-3 pt-1', className), children: [_jsx("span", { className: "text-caption font-medium text-muted-foreground", children: children }), _jsx("span", { className: "h-px flex-1 bg-border", "aria-hidden": "true" })] }));
}
//# sourceMappingURL=field.js.map